import React, { useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { useToast } from '../../../components/Toast';
import CompanyModuleShadowPanel from './CompanyModuleShadowPanel';
import TenantModuleEnforcementPanel from './TenantModuleEnforcementPanel';
import TenantModulePermissionPreviewModal from './TenantModulePermissionPreviewModal';
import TenantModuleTable from './TenantModuleTable';
import {
  chipStyle,
  chipWrapStyle,
  panelSubStyle,
  panelTitleStyle,
  rowMetaStyle,
  rowTitleStyle,
  summaryCardStyle,
  summaryGridStyle,
  summaryLabelStyle,
  summaryValueStyle,
} from './dashboardStyles';

function badgeStyle(color, filled = false) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 6,
    border: `1px solid ${color}`,
    background: filled ? color : 'var(--bg-card)',
    color: filled ? '#fff' : color,
    padding: '2px 7px',
    fontSize: 11,
    fontWeight: 900,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
  };
}

function tenantButtonStyle(active) {
  return {
    border: `1px solid ${active ? '#2563eb' : 'var(--border)'}`,
    background: active ? 'rgba(37,99,235,0.12)' : 'var(--bg-card)',
    color: active ? '#2563eb' : 'var(--text)',
    borderRadius: 8,
    padding: '8px 10px',
    cursor: 'pointer',
    textAlign: 'left',
    minWidth: 150,
  };
}

function moduleFilterButtonStyle(active) {
  return {
    border: `1px solid ${active ? '#2563eb' : 'var(--border)'}`,
    borderRadius: 7,
    background: active ? 'rgba(37,99,235,0.12)' : 'var(--bg-card)',
    color: active ? '#2563eb' : 'var(--text-sub)',
    padding: '5px 8px',
    fontSize: 12,
    fontWeight: 850,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };
}

function moduleSearchStyle() {
  return {
    width: 220,
    maxWidth: '100%',
    border: '1px solid var(--border)',
    borderRadius: 7,
    background: 'var(--bg-card)',
    color: 'var(--text)',
    padding: '6px 9px',
    fontSize: 12,
    fontWeight: 750,
    outline: 'none',
  };
}

function SummaryCard({ label, value, help }) {
  return (
    <div style={summaryCardStyle}>
      <div style={summaryLabelStyle}>{label}</div>
      <div style={summaryValueStyle}>{value ?? '-'}</div>
      {help ? <div style={{ ...rowMetaStyle, marginTop: 4 }}>{help}</div> : null}
    </div>
  );
}

function TenantSelector({ tenants, selectedCode, onSelect }) {
  return (
    <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 2 }}>
      {tenants.map((tenant) => (
        <button
          key={tenant.code}
          type="button"
          style={tenantButtonStyle(selectedCode === tenant.code)}
          onClick={() => onSelect(tenant.code)}
        >
          <div style={{ ...rowTitleStyle, fontSize: 13 }}>{tenant.name}</div>
          <div style={rowMetaStyle}>
            {tenant.code} · ON {tenant.enabledModuleCount}/{tenant.moduleCount} · 직원 {tenant.employeeCount}
          </div>
        </button>
      ))}
    </div>
  );
}

export default function TenantModuleView({ overview, onRefresh }) {
  const { showToast } = useToast();
  const data = overview?.tenantModuleOverview;
  const tenants = data?.tenants || [];
  const [selectedCode, setSelectedCode] = useState(tenants[0]?.code || 'PINE');
  const [savingKey, setSavingKey] = useState('');
  const [previewLoadingKey, setPreviewLoadingKey] = useState('');
  const [permissionPreview, setPermissionPreview] = useState(null);
  const [moduleFilter, setModuleFilter] = useState('all');
  const [moduleSearch, setModuleSearch] = useState('');
  const selectedTenant = tenants.find((tenant) => tenant.code === selectedCode) || tenants[0] || null;
  const selectedModules = selectedTenant?.modules || [];
  const categoryFilters = Array.from(new Set(selectedModules.map((module) => module.category).filter(Boolean)))
    .sort((a, b) => a.localeCompare(b, 'ko-KR'));
  const moduleFilters = [
    { key: 'all', label: '전체' },
    { key: 'enabled', label: '사용 중' },
    { key: 'disabled', label: '꺼짐' },
    { key: 'issue', label: '확인필요' },
    { key: 'sensitive', label: '민감' },
    ...categoryFilters.map((category) => ({ key: `category:${category}`, label: category })),
  ];
  const filteredModules = selectedModules.filter((module) => {
    const query = moduleSearch.trim().toLowerCase();
    if (moduleFilter === 'enabled' && !module.isEnabled) return false;
    if (moduleFilter === 'disabled' && module.isEnabled) return false;
    if (moduleFilter === 'issue' && module.shadowCheck?.type === 'aligned') return false;
    if (moduleFilter === 'sensitive' && !['sensitive', 'restricted'].includes(module.sensitivity)) return false;
    if (moduleFilter.startsWith('category:') && module.category !== moduleFilter.replace('category:', '')) return false;
    if (!query) return true;
    return [module.code, module.name, module.description, module.category, module.sensitivity]
      .some((value) => String(value || '').toLowerCase().includes(query));
  });

  async function handleToggle(tenant, module, options = {}) {
    const nextEnabled = !module.isEnabled;
    const sensitive = ['sensitive', 'caution'].includes(module.sensitivity);
    const warning = [
      `${tenant.name}의 ${module.name} 기능을 SaaS 원장에서 ${nextEnabled ? '켜기' : '끄기'}로 저장합니다.`,
      '이 단계는 고객사 기능 원장만 바꾸고, 직원 권한은 자동으로 배정/회수하지 않습니다.',
    ];
    if (sensitive || module.assignedUserCount > 0) {
      warning.push(`현재 연결 권한 ${module.permissionCount}개, 배정 직원 ${module.assignedUserCount}명이 보입니다.`);
    }
    if (!window.confirm(warning.join('\n'))) return;

    const key = `${tenant.code}:${module.code}`;
    setSavingKey(key);
    const result = await apiClient.put('/api/admin/saas/tenant-modules', {
      tenantCode: tenant.code,
      moduleCode: module.code,
      isEnabled: nextEnabled,
      note: options.note || '업무/기능 배정 화면에서 수동 변경',
    });
    setSavingKey('');

    if (!result.ok) {
      showToast(result.data?.message || '고객사 기능 배정 저장에 실패했습니다.', 'error');
      return;
    }
    showToast(`${tenant.name} / ${module.name} 기능을 ${nextEnabled ? '사용 중' : '꺼짐'}으로 저장했습니다.`, 'success');
    if (onRefresh) await onRefresh();
  }

  async function handleMark(tenant, module, options = {}) {
    const label = options.label || '정리 표시';
    if (!window.confirm(`${tenant.name}의 ${module.name} 기능을 "${label}"로 표시합니다.\n직원 권한은 자동으로 배정/회수하지 않고, SaaS 원장 메모와 감사 로그만 남깁니다.`)) return;
    const key = `${tenant.code}:${module.code}`;
    setSavingKey(key);
    const result = await apiClient.put('/api/admin/saas/tenant-modules', { tenantCode: tenant.code, moduleCode: module.code, isEnabled: module.isEnabled, note: options.note || `[SHADOW_REVIEW_LATER] Shadow 정리: ${label}` });
    setSavingKey('');
    if (!result.ok) {
      showToast(result.data?.message || '고객사 기능 정리 표시 저장에 실패했습니다.', 'error');
      return;
    }
    showToast(`${tenant.name} / ${module.name} 기능을 ${label}로 표시했습니다.`, 'success');
    if (onRefresh) await onRefresh();
  }

  async function handlePreview(tenant, module) {
    const key = `${tenant.code}:${module.code}`;
    setPreviewLoadingKey(key);
    const params = new URLSearchParams({ tenantCode: tenant.code, moduleCode: module.code });
    const result = await apiClient.get(`/api/admin/saas/tenant-modules/permission-preview?${params.toString()}`);
    setPreviewLoadingKey('');

    if (!result.ok) {
      showToast(result.data?.message || '권한 미리보기를 불러오지 못했습니다.', 'error');
      return;
    }
    setPermissionPreview(result.data?.preview || null);
  }

  if (!data) {
    // 테넌트 관리자: 운영자용 예산 개요(tenantModuleOverview)는 없지만 자기 테넌트의
    // 회사 모듈 원장(companyModuleShadowReport)이 오면 회사별 메뉴 토글만 노출.
    if (overview?.companyModuleShadowReport) {
      return (
        <CompanyModuleShadowPanel
          report={overview.companyModuleShadowReport}
          onRefresh={onRefresh}
          budgetScoped
        />
      );
    }
    return (
      <div style={{ ...summaryCardStyle, padding: 12 }}>
        <h3 style={panelTitleStyle}>고객사 기능 배정</h3>
        <div style={panelSubStyle}>고객사 기능 배정 기준을 불러오지 못했습니다.</div>
      </div>
    );
  }

  return (
    <section style={{ display: 'grid', gap: 12 }}>
      <div style={{ ...summaryCardStyle, padding: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <div>
            <h3 style={panelTitleStyle}>고객사 기능 배정</h3>
            <div style={panelSubStyle}>
              이 화면은 고객사 기능 원장을 저장합니다. 기존 직원 권한 자동 배정/회수는 아직 하지 않습니다.
            </div>
          </div>
          <div style={rowMetaStyle}>기준 {data.generatedAt || '-'}</div>
        </div>

        <div style={{ ...summaryGridStyle, marginTop: 10 }}>
          <SummaryCard label="고객사" value={data.summary?.tenantCount} help="PINE 포함 현재 기준" />
          <SummaryCard label="모듈 종류" value={data.summary?.moduleCount} help="SaaS 기능 묶음" />
          <SummaryCard label="켜진 기능" value={data.summary?.enabledCount} help="고객사별 ON 합계" />
          <SummaryCard label="민감 기능 ON" value={data.summary?.sensitiveEnabledCount} help="급여·로그 등 주의 대상" />
          <SummaryCard label="Shadow 확인" value={data.summary?.shadowIssueCount ?? data.summary?.enforcementIssueCount} help="원장과 현재 권한 노출 차이" />
        </div>
      </div>

      <CompanyModuleShadowPanel report={overview?.companyModuleShadowReport} onRefresh={onRefresh} />

      <TenantSelector tenants={tenants} selectedCode={selectedTenant?.code} onSelect={setSelectedCode} />

      {selectedTenant && (
        <>
            <TenantModuleEnforcementPanel
              tenant={selectedTenant}
              modules={selectedModules}
              savingKey={savingKey}
              previewLoadingKey={previewLoadingKey}
              onToggle={handleToggle}
              onMark={handleMark}
              onPreview={handlePreview}
            />
          <div style={{ ...summaryCardStyle, padding: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
              <div>
                <h3 style={panelTitleStyle}>
                  {selectedTenant.name} <span style={rowMetaStyle}>({selectedTenant.code})</span>
                </h3>
                <div style={panelSubStyle}>
                  직원 {selectedTenant.employeeCount}명 · 사용 중 {selectedTenant.enabledModuleCount}개 · 꺼짐 {selectedTenant.disabledModuleCount}개
                  {selectedTenant.shadowIssueCount > 0 ? ` · Shadow 확인 ${selectedTenant.shadowIssueCount}개` : ''}
                </div>
              </div>
              <span style={badgeStyle(selectedTenant.tenantType === 'platform' ? '#7c3aed' : '#2563eb')}>
                {selectedTenant.tenantType === 'platform' ? '플랫폼 운영사' : '고객사'}
              </span>
            </div>
            <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
              <div style={chipWrapStyle}>
                {moduleFilters.map((filter) => (
                  <button
                    key={filter.key}
                    type="button"
                    style={moduleFilterButtonStyle(moduleFilter === filter.key)}
                    onClick={() => setModuleFilter(filter.key)}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
              <input
                value={moduleSearch}
                onChange={(event) => setModuleSearch(event.target.value)}
                placeholder="모듈 검색"
                style={moduleSearchStyle()}
              />
            </div>
            <div style={{ marginTop: 8 }}>
              <span style={chipStyle}>
                표시 {filteredModules.length}개 / 전체 {selectedModules.length}개
              </span>
            </div>
            <TenantModuleTable
              modules={filteredModules}
              tenant={selectedTenant}
              savingKey={savingKey}
              previewLoadingKey={previewLoadingKey}
              onToggle={handleToggle}
              onPreview={handlePreview}
            />
          </div>
        </>
      )}
      <TenantModulePermissionPreviewModal
        preview={permissionPreview}
        onClose={() => setPermissionPreview(null)}
      />
    </section>
  );
}
